/*
 * Home's sections (CLAUDE.md §4 "Home", Saif 21 Oct 2026 evening: Loyalty and Offers folded into Home). Each is on or
 * off and in Saif's order (Admin › Settings › Home, setting `home`); the app draws them top to bottom under the greeting.
 * A section still shows only when it has something to show (signed in, its feature
 * on, not empty): switching it on never forces it. "Live order" and "Your usual" never show together: while an order is
 * on the go the live tile takes the usual's place.
 */

export interface HomeSectionDef { id: string; label: string; sub: string; /** Off at the start (a switch Saif may turn on). */ off?: true }

export const HOME_SECTIONS = [
  { id: 'greeting', label: 'Greeting', sub: '"Good afternoon, Priya" as the page title, like Order and Account have (Saif, 22 Oct)' },
  { id: 'today', label: 'Today at Tikkas (big)', sub: 'The big picture block with Order now; off since the status line took its place', off: true },
  { id: 'card', label: 'The Tikkas card', sub: 'Credit and points as the focus, the name and badge, the three-dot menu (Activity, Wallet, how points work)' },
  { id: 'pills', label: 'Top up · Rewards · Earn', sub: 'The glass pill row under the card; each opens its sheet' },
  { id: 'live_order', label: 'Your order, live', sub: 'Only while one of their orders is on the go; it takes Your usual’s place' },
  { id: 'usual', label: 'Your usual', sub: 'One row with their usual and a one-tap reorder, when no order is on the go' },
  { id: 'whats_new', label: 'What’s new', sub: 'Your picture cards, when one is showing' },
] as const satisfies readonly HomeSectionDef[];

export const HOME_SECTION_IDS = HOME_SECTIONS.map((s) => s.id) as unknown as readonly HomeSectionId[];
export type HomeSectionId = (typeof HOME_SECTIONS)[number]['id'];
export interface HomeSection { id: HomeSectionId; enabled: boolean }
export interface HomeSetting { blocks: HomeSection[] }

export const DEFAULT_HOME: HomeSetting = { blocks: HOME_SECTIONS.map((s) => ({ id: s.id, enabled: !('off' in s && s.off) })) };

export const isHomeSection = (v: unknown): v is HomeSectionId => typeof v === 'string' && (HOME_SECTION_IDS as readonly string[]).includes(v);

/**
 * The stored order, read leniently: anything unknown dropped and any section it lacks added at the end, on (a new
 * section shows up). A layout saved before the card came to Home (no `card` in it) goes back to the start, since its
 * order and switches were about a different page.
 */
export function normaliseHome(raw: unknown): HomeSetting {
  const list = raw && typeof raw === 'object' && Array.isArray((raw as { blocks?: unknown }).blocks) ? (raw as { blocks: unknown[] }).blocks : [];
  const out: HomeSection[] = [];
  for (const b of list) {
    const r = (b ?? {}) as { id?: unknown; enabled?: unknown };
    const id = r.id === 'status' ? 'greeting' : r.id; // the greeting-and-status section of 22 Oct morning is the greeting now
    if (isHomeSection(id) && !out.some((x) => x.id === id)) out.push({ id, enabled: r.id === 'status' ? true : r.enabled !== false });
  }
  if (!out.some((x) => x.id === 'card')) return { blocks: DEFAULT_HOME.blocks.map((b) => ({ ...b })) };
  for (const s of DEFAULT_HOME.blocks) if (!out.some((x) => x.id === s.id)) out.push({ ...s });
  return { blocks: out };
}

/** Which sections to draw, in order: the live tile replaces the usual while an order is on the go (never both). */
export function homeSectionsToDraw(blocks: readonly HomeSection[], o: { liveOrder: boolean }): HomeSectionId[] {
  return blocks.filter((b) => b.enabled).map((b) => b.id).filter((id) => (o.liveOrder ? id !== 'usual' : id !== 'live_order'));
}
